/**
 * Who gets EVERY business account in the organization, regardless of their
 * `team_member_business_accounts` rows.
 *
 * This mirrors the authoritative backend gate in
 * `BusinessAccountService.findAll`:
 *
 *   const hasFullAccess = isOwner || isSuperAdmin || (roleRaw === 'ADMIN' && !isAgency);
 *   if (!hasFullAccess && userId) { innerJoin(teamMemberBusinessAccounts) ... }
 *
 * i.e. for an owner, a super admin, or an ADMIN, the assignment table is not
 * joined at all — every account in the org is returned. The stored assignment
 * rows are simply ignored for those members.
 *
 * WHY THIS EXISTS: the Edit / Invite member drawers used to render an editable
 * "Business Accounts Access" list for admins, silently force-select every
 * account in an effect, and accept clicks that could never take effect. The
 * visible symptom was "I deselected an account and saved, reopened, and it was
 * back" — the save had worked (the DB really did hold one row), but the drawer
 * overwrote the selection on the next open, AND a later save would persist the
 * forced-all set, quietly re-granting what the admin had revoked.
 *
 * Keeping the rule in one pure place is what stops the two drawers drifting
 * apart, and what lets the UI explain itself instead of offering a choice the
 * server will not honour.
 */

export interface OrgWideAccessSubject {
  /** The member's role, in any casing. */
  role?: string | null;
  /** `profiles.is_organization_owner` for the member. */
  isOrganizationOwner?: boolean | null;
  /** `profiles.is_super_admin` for the member. */
  isSuperAdmin?: boolean | null;
  /**
   * `users.is_agency`. Present for parity with the backend expression only —
   * an agency ADMIN is deliberately NOT given org-wide access there. Team
   * members of a customer organization are never agency users (those live in
   * the agency portal), so this defaults to false and is almost always
   * irrelevant here. Pass it explicitly if a caller ever does know it.
   */
  isAgency?: boolean | null;
}

/** Roles that the backend treats as org-wide. Only ADMIN today. */
const ORG_WIDE_ROLES = new Set(['ADMIN']);

/**
 * True when the member's per-account selection cannot restrict anything,
 * because the backend hands them every account in the organization anyway.
 *
 * Defensive about shape: the flags arrive from several sources (`profiles`,
 * a Drizzle row, a JSON blob) and have been seen as `1`/`'true'` as well as
 * booleans, so anything truthy counts — the same tolerance
 * `MinimalEditDialog.parseAccessFlag` already applies to permission flags.
 */
export function hasOrgWideBusinessAccountAccess(subject: OrgWideAccessSubject | null | undefined): boolean {
  if (!subject) return false;

  if (isTruthyFlag(subject.isSuperAdmin)) return true;
  if (isTruthyFlag(subject.isOrganizationOwner)) return true;

  const role = (subject.role ?? '').toString().trim().toUpperCase();
  if (!ORG_WIDE_ROLES.has(role)) return false;

  // An agency ADMIN is excluded by the backend expression, so exclude it here
  // too rather than promising access the server will not grant.
  return !isTruthyFlag(subject.isAgency);
}

function isTruthyFlag(value: unknown): boolean {
  return value === true || value === 1 || value === '1' || value === 'true';
}

/**
 * Copy shown in place of the editable list when access is org-wide. It names
 * the way OUT (change the role), because otherwise an admin trying to restrict
 * a colleague has no idea why the checkboxes stopped responding.
 */
export const ORG_WIDE_ACCESS_NOTE =
  'This member has access to every business account in the organization, so it cannot be narrowed here. Change their role to User or Agent to limit which accounts they can access.';

/**
 * The selection to display/submit for a member. Returns EVERY account id when
 * access is org-wide, so what the drawer shows, what the summary line counts,
 * and what a save persists can never disagree with each other.
 */
export function resolveSelectedBusinessAccountIds(
  args: {
    subject: OrgWideAccessSubject;
    allAccountIds: readonly string[];
    storedSelection: readonly string[];
  },
): string[] {
  const { subject, allAccountIds, storedSelection } = args;
  if (hasOrgWideBusinessAccountAccess(subject)) return [...(allAccountIds ?? [])];
  return [...(storedSelection ?? [])];
}
